import { addZero } from "@/lib/commonService";
import murmurhash3 from "@/lib/murmurhash3";

// 图片旋转
interface IImgRotate {
    degree: number;
    imgW: number;
    imgH: number;
}

export function imgRotate({ degree, imgW, imgH }: IImgRotate) {
    // 起点
    let obj = { x: 0, y: 0 };
    // 偶数代表旋转180， 360等
    const isEven = degree % 2 === 0;
    if (isEven) {
        return { x: -imgW / 2, y: -imgH / 2, cvsW: imgW, cvsH: imgH };
    } else {
        return { x: -imgW / 2, y: -imgH / 2, cvsW: imgH, cvsH: imgW };
    }
}

/**
 * 压缩图片文件
 * @param {*} file
 * @param {*} acceptPdf
 * @param {*} degree
 * @param {*} noZip
 * @param {*} upWebp
 */
interface ICompress {
    img: Blob;
    webpImg: Blob | string;
}
export function compress(
    file: File,
    acceptPdf: boolean,
    degree = 0,
    noZip: boolean,
    upWebp: boolean,
): Promise<ICompress> {
    return new Promise((resolve, rej) => {
        // 已经过掉pdf的处理了，后面用到再看要怎么办
        // if (acceptPdf && file.type.includes("pdf")) resolve(file);
        imgCompress(
            file,
            degree,
            noZip,
            upWebp,
            (res: string, webpBase64: string) => {
                // 参数 jpg的base64   webp的base64
                const img = convertBase64UrlToBlob(res);
                // 是否有webp图片
                const webpImg = webpBase64
                    ? convertBase64UrlToBlob(webpBase64, "image/webp")
                    : "";
                resolve({ img, webpImg });
            },
        );
    });
}

// 使用canvas压缩图片,转化图片格式
function canvasData(
    re: string | ArrayBuffer | null,
    percent: number,
    degree: number,
    cb: Function,
    fileBytes: number,
    noZip: boolean,
    upWebp: boolean,
) {
    const newImg = new Image();
    newImg.crossOrigin = "anonymous";
    newImg.src = re as string;
    let imgWidth;
    let imgHeight;
    imgWidth = newImg.width * percent;
    imgHeight = newImg.height * percent;
    const canvas = document.createElement("canvas");

    let obj = {
        x: -imgWidth / 2,
        y: -imgHeight / 2,
        cvsW: imgWidth,
        cvsH: imgHeight,
    };
    if (degree > 0) {
        obj = imgRotate({ imgW: imgWidth, imgH: imgHeight, degree });
    }
    canvas.width = obj.cvsW;
    canvas.height = obj.cvsH;
    const ctx = canvas.getContext("2d");
    ctx?.translate(obj.cvsW / 2, obj.cvsH / 2);
    // 旋转图片
    ctx?.rotate(degree * (Math.PI / 2));
    ctx?.drawImage(newImg, obj.x, obj.y, imgWidth, imgHeight);
    const base64 = canvas.toDataURL(
        "image/jpeg",
        getImgComporessQuality(fileBytes, noZip),
    );
    //判断是否需要webp格式文件
    const webpBase64 =
        upWebp && isWebkitBrowser()
            ? canvas.toDataURL(
                  "image/webp",
                  getImgComporessQuality(fileBytes, noZip),
              )
            : "";
    cb(base64, webpBase64);
}

// 压缩图片
function imgCompress(
    file: File,
    degree: number,
    noZip: boolean,
    upWebp: boolean,
    cb: Function,
) {
    const ready = new FileReader();
    const percent = percentCtr(file.size, 1.0, noZip);
    ready.addEventListener(
        "load",
        () => {
            canvasData(
                ready.result,
                percent,
                degree,
                cb,
                file.size,
                noZip,
                upWebp,
            );
        },
        false,
    );
    file && ready.readAsDataURL(file);
}

// 压缩比例
function percentCtr(fileSize: number, percent = 1.0, noZip: boolean) {
    fileSize = fileSize / 1024 / 1024;
    if (noZip) return percent;
    switch (true) {
        case fileSize >= 4:
            percent = 0.7;
            break;
        default:
            percent = 1;
            break;
    }
    return percent;
}

/**
 * base64转图片，图片预览
 * @param {*} urlData base64字节
 * @param {*} webpBase64 webp格式base64字节
 */
function convertBase64UrlToBlob(urlData: string, type = "image/png") {
    //去掉url的头，并转换为byte
    const bytes = window.atob(urlData.split(",")[1]);
    //处理异常,将ascii码小于0的转换为大于0
    var ab = new ArrayBuffer(bytes.length);
    var ia = new Uint8Array(ab);
    for (var i = 0; i < bytes.length; i++) {
        ia[i] = bytes.charCodeAt(i);
    }
    return new Blob([ab], { type: type });
}

//获取图片压缩质量
function getImgComporessQuality(size: number, noZip: boolean) {
    if (!size || noZip) return 1;
    //获取图片大小 多少 M
    const fileSize = size / 1024 / 1024;
    //一个map，不同的大小，压缩比不一样
    if (fileSize < 1) return 1;
    //获取整数
    const intNum = parseInt(fileSize.toString(), 10);
    //压缩率数组 1M不压缩 2M*0.9
    const rateArr = [1, 0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3];
    return rateArr[intNum] || rateArr[rateArr.length - 1];
}

//判断是否webkit内核浏览器 canvas.toDataURLz转化webp，只有webkit内核支持,但是好像是有
function isWebkitBrowser() {
    return (
        (window &&
            window.navigator.userAgent.indexOf("Chrome") > -1 &&
            navigator.userAgent.indexOf("Edge") == -1) ||
        ""
    );
}

interface IFolderProps {
    folder: string;
    timestamp: number;
}
// 拼接路径
export function getS3Folder(
    folder = "images/users",
    time = false,
): IFolderProps | string {
    const date = new Date();
    const timestamp = Math.floor(Date.parse(date.toString()) / 1000);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const floder = `${folder}/${year}/${addZero(month)}${addZero(day)}/`;
    if (time) return { folder: floder, timestamp };
    return floder;
}

// 根据图片名获取128位的图片hash，生成唯一图片名字
export function getFileHash(fileName: string, small = false) {
    const time = Date.parse(new Date().toString());
    fileName += time;
    if (small) {
        return "small-" + murmurhash3.x86.hash128(fileName);
    }
    return murmurhash3.x86.hash32(fileName);
}
